<template>
  <modal-sliding background-color="bg-gray-100">
    <div class="px-6 py-4 overflow-y-auto h-full pb-[200px]" id="zelty-setup-modal">

      <div class="mb-10">
        <h2 class="font-bold text-2xl mb-2">Connectez votre caisse Zelty à Delicity</h2>
        <span class="text-neutral-700">Suivez les étapes ci-dessous pour activer l’intégration.</span>
      </div>


      <div class="bg-orange-200 px-5 py-3 rounded-md font-medium items-center flex gap-4" v-if="currentMerchant.integrations.some(e => e.type === 'hubrise')">
        <i class="material-icons text-orange-600">warning</i>
        <span>
          Attention, vous avez une intégration Hubrise active sur votre compte. <br>
          -> Si Hubrise est également connecté à Zelty, vous pourrez recevoir des commande en double... <br>
          Pensez à déconnecter Hubrise après avoir fini cette intégration.
        </span>
      </div>


      <TransitionGroup
          name="list"
          tag="div"
          class="relative"
      >


        <div class="flex gap-4 py-5" :class="{'thread': currentStep>=2}" key="request_api_key">
          <div>
            <div class="h-11 relative z-50 border-4 border-gray-100 text-center w-11 rounded-full bg-primary flex items-center justify-center text-xl font-bold text-white">1</div>
          </div>
          <div class="pt-2 flex flex-col items-start gap-1">
            <span class="font-bold uppercase text-xl">Récupérez votre clé API Zelty</span>
            <span class="text-neutral-700">Pour commencer, vous devez obtenir votre clé API Zelty.
              <br>Cliquez sur le bouton ci-dessous pour ouvrir le formulaire de demande côté Zelty.
            </span>
            <div class="flex gap-2 mt-2">
              <a href="https://zelty.fr/forms/api" v-if="currentStep===1" target="_blank" class="flex items-center btn bg-neutral-200 text-neutral-800">Obtenir ma clé API Zelty</a>
              <button :disabled="currentStep!==1" class="btn bg-primary text-white" @click="currentStep=2">J'ai déjà une clé API Zelty</button>
            </div>
          </div>
        </div>




        <div class="flex gap-4 py-5" :class="{'thread': currentStep>=3}" v-if="currentStep>=2" key="insert_api_key">
          <div>
            <div class="h-11 relative z-50 border-4 border-gray-100 text-center w-11 rounded-full bg-primary flex items-center justify-center text-xl font-bold text-white">2</div>
          </div>
          <div class="pt-2 flex flex-col items-start gap-1">
            <span class="font-bold uppercase text-xl">Entrez votre clé API</span>
            <span class="text-neutral-700">Une fois votre clé récupérée, collez-la ici pour permettre à Delicity de se connecter à votre compte Zelty.</span>
            <div class="flex items-center gap-1 text-red-600 mt-4" v-if="apiHaveError"><i class="material-icons text-2xl mr-1">error</i>Erreur, il semblerais que la clés API n'est pas bonne</div>

            <div class="flex gap-2 mt-2">
              <input type="text" v-model="zeltyConfig.api_key" :disabled="currentStep!==2" class="form-input">
              <button :disabled="currentStep!==2" class="btn bg-primary text-white" @click="nextStep(3)">Valider</button>
            </div>
          </div>
        </div>



        <div class="flex gap-4 py-5" :class="{'thread': currentStep>=4}" v-if="currentStep>=3" key="select_restaurant">
          <div>
            <div class="h-11 relative z-50 border-4 border-gray-100 text-center w-11 rounded-full bg-primary flex items-center justify-center text-xl font-bold text-white">3</div>
          </div>
          <div class="pt-2 flex flex-col items-start gap-1">
            <span class="font-bold uppercase text-xl">Sélectionnez votre restaurant</span>
            <span class="text-neutral-700">Choisissez le restaurant que vous souhaitez connecter à votre compte Delicity.</span>
            <div class="flex gap-2 mt-2">
              <select class="form-select min-w-[200px]" :disabled="currentStep!==3" v-model="zeltyConfig.restaurantId">
                <option :value="restaurant.id" v-for="restaurant in restaurants">{{restaurant.name}}</option>
              </select>
              <button class="btn bg-primary text-white" :disabled="!zeltyConfig.restaurantId || currentStep!==3" @click="nextStep(4)">Valider</button>
            </div>
          </div>
        </div>



        <div class="flex gap-4 py-5" :class="{'thread': currentStep>=5}" v-if="currentStep>=4" key="select_catalog">
          <div>
            <div class="h-11 relative z-50 border-4 border-gray-100 text-center w-11 rounded-full bg-primary flex items-center justify-center text-xl font-bold text-white">4</div>
          </div>
          <div class="pt-2 flex flex-col items-start gap-1">
            <span class="font-bold uppercase text-xl">Choisissez le menu à synchroniser</span>
            <span class="text-neutral-700">Sélectionnez le menu à utiliser pour vos commandes Delicity.</span>
            <span class="text-neutral-700">Nous vous conseillons de créer un menu dédié appelé <b>“Delicity”</b> sur Zelty pour simplifier la gestion par la suite.</span>
            <div class="flex items-center gap-1 text-red-600 mt-4"><i class="material-icons text-2xl mr-1">info</i> Attention, En utilisant Zelty, Vous allez écraser votre le menu actuel Delicity s’il existe déjà.</div>
            <div class="flex gap-2 mt-2">
              <select class="form-select min-w-[200px]" :disabled="isSaving || currentStep!==4" v-model="zeltyConfig.menuId">
                <option :value="menu.id" v-for="menu in menus">{{menu.name}}</option>
              </select>
              <button @click="refreshCatalogs()"  class="btn bg-neutral-200 w-16 flex items-center justify-center p-0" :disabled="isSaving || currentStep!==4"><i class="material-icons">sync</i></button>
              <button class="btn bg-primary text-white" @click="nextStep(5)" :disabled="isSaving || currentStep!==4">Continuer</button>
            </div>
          </div>
        </div>


        <div class="flex gap-4 py-5" :class="{'thread': currentStep>=6}" v-if="currentStep>=5" key="select_payment_method">
          <div>
            <div class="h-11 relative z-50 border-4 border-gray-100 text-center w-11 rounded-full bg-primary flex items-center justify-center text-xl font-bold text-white">4</div>
          </div>
          <div class="pt-2 flex flex-col items-start gap-1">
            <span class="font-bold uppercase text-xl">Choisissez un mode de règlement</span>
            <span class="text-neutral-700">Afin de bien pouvoir identifier les paiement Delicity sur Zelty, merci de bien vouloir créer un mode de paiement sur votre backoffice <a
                href="https://support.zelty.fr/hc/fr/articles/360019904691-M%C3%A9thodes-de-paiement" target="_blank" class="text-blue-500">Plus d'informations ici</a></span>
            <div class="flex gap-2 mt-2">
              <select class="form-select min-w-[200px]" :disabled="isSaving || currentStep!==5" v-model="zeltyConfig.transactionMethodId">
                <option :value="pm.id" v-for="pm in paymentMethods">{{pm.name}}</option>
              </select>
              <button @click="refreshPaymentMethods()" class="btn bg-neutral-200 w-16 flex items-center justify-center p-0" :disabled="isSaving || currentStep!==5"><i class="material-icons">sync</i></button>
              <button class="btn bg-primary text-white" @click="nextStep(6)" :disabled="isSaving || currentStep!==5">Continuer</button>
            </div>
          </div>
        </div>


        <div class="flex gap-4 py-5" v-if="currentStep>=6" key="end">
          <div>
            <div class="h-11 relative z-50 border-4 border-gray-100 text-center w-11 rounded-full bg-green-600 flex items-center justify-center text-xl font-bold text-white">
              <i class="material-icons">check</i>
            </div>
          </div>
          <div class="pt-2 flex flex-col items-start gap-1">
            <span class="font-bold uppercase text-xl">Syncronisation en cours... </span>
            <span class="text-neutral-700">(Cela peut prendre quelques secondes, Vous pouvez fermer cette page.)</span>
            <span class="text-neutral-700">Désormais, votre caisse Zelty pilotera le menu : <b>Chaque modification devra se faire depuis Zelty, et non plus sur Delicity.</b> <br>
              Cela garantit une reconnaissance comptable fiable des produits lors des commandes.</span>
          </div>
        </div>



      </TransitionGroup>

    </div>


  </modal-sliding>
</template>

<script>
import ModalSliding from "@/components/Ui/Modals/ModalSliding.vue";
import {mapGetters} from "vuex";
import {useVfm} from "vue-final-modal";
import {
  getZeltyMenus,
  getZeltyPaymentMethods,
  getZeltyRestaurants,
  saveZeltyIntegration
} from "@/services/Integration/ZeltyService";

export default {
  components: {ModalSliding},
  data(){
    return {
      currentStep: 1,
      zeltyConfig: {
        api_key: null,
        restaurantId: null,
        menuId: null,
        transactionMethodId: null
      },
      apiHaveError: false,
      restaurants: [],
      paymentMethods: [],
      menus: [],
      isSaving: false
    }
  },
  computed: {
    ...mapGetters({
      currentMerchant: 'currentMerchant'
    }),
  },
  methods:{
    async refreshCatalogs(){
      this.menus = await getZeltyMenus(this.currentMerchant.id, this.zeltyConfig.api_key);
    },
    async refreshPaymentMethods(){
      this.paymentMethods = await getZeltyPaymentMethods(this.currentMerchant.id, this.zeltyConfig.api_key);
    },
    async nextStep(step){
      this.currentStep = step;
      this.apiHaveError = false;
      if(step === 3){
        try {
          this.restaurants = await getZeltyRestaurants(this.currentMerchant.id, this.zeltyConfig.api_key);
          if(this.restaurants.length>0){
            this.zeltyConfig.restaurantId = this.restaurants[0].id;
          }
        }
        catch (e){
         this.apiHaveError = true;
         this.currentStep = 2;
        }
      }
      else if(step === 4){
        this.menus = await getZeltyMenus(this.currentMerchant.id, this.zeltyConfig.api_key);
        if(this.menus.length>0){
          this.zeltyConfig.menuId = this.menus[0].id;
        }
      }
      else if(step === 5){
        this.paymentMethods = await getZeltyPaymentMethods(this.currentMerchant.id, this.zeltyConfig.api_key);
        if(this.paymentMethods.length>0){
          this.zeltyConfig.transactionMethodId = this.paymentMethods[0].id;
        }

        // scroll bottom
        const element = document.getElementById('zelty-setup-modal');
        element.scrollTop = element.scrollHeight;
        setTimeout(() => {
          element.scrollTop = element.scrollHeight;
        }, 100);
      }
      else if(step === 6){
        await this.save();
      }
    },
    async save(){
      this.isSaving = true;
      const selectedMenu = this.menus.find(menu => menu.id === this.zeltyConfig.menuId);
      setTimeout(() => this.$store.dispatch('getMerchantsAction'), 300);
      await saveZeltyIntegration(
          this.currentMerchant.id,
          this.zeltyConfig.api_key,
          this.zeltyConfig.restaurantId,
          this.zeltyConfig.menuId,
          selectedMenu.name,
          this.zeltyConfig.transactionMethodId
      )
      await this.$store.dispatch('getMerchantsAction');
      localStorage.setItem('menu:aggregator:sync', (new Date()/1000)+15);
      this.isSaving = true;
    },
    close(){
      useVfm().closeAll();
    }
  },
  async mounted() {

  }
}
</script>


<style>
.thread{
  position: relative;
}
.thread:before{
  content: '';
  display: block;
  position: absolute;
  height: 100%;
  width: 2px;
  z-index: 0;
  left: calc(2.75rem / 2 - 1px);
  animation: threadHeight 0.8s ease-out forwards;
}

@keyframes threadHeight {
  from {
    height: 0%;
    background-color: #313131;
  }
  to {
    height: 100%;
    background-color: #ddd;
  }
}

.list-move,
.list-enter-active,
.list-leave-active {
  transition: all 0.3s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

/* Les éléments quittant le flux sont en absolu */
.list-leave-active {
  position: absolute;
}

</style>
